import React from "react";
import { Dialog } from "../../../ui/Dialog";
import { Button } from "../../../ui/Button";
import type { DeleteProgress, Document, FailedDeletion } from "../types";

/** Enough filenames to recognise the selection without turning the dialog into a list. */
const MAX_NAMED_FILES = 5;

interface DeleteDocumentDialogProps {
  documents: Document[];
  progress: DeleteProgress | null;
  /** Documents the API refused on the last attempt, with its reason for each. */
  failures: FailedDeletion[];
  onCancel: () => void;
  onConfirm: () => void;
}

function summariseFilenames(documents: Document[]): string {
  const named = documents.slice(0, MAX_NAMED_FILES).map((d) => d.filename);
  const remaining = documents.length - named.length;
  return remaining > 0
    ? `${named.join(", ")} +${remaining} more`
    : named.join(", ");
}

/**
 * Confirmation for deleting context documents, one or many.
 *
 * Deleting reaches the retrieval collection as well as our own records, so the
 * model stops drawing on the document entirely — there is no undo, and the file
 * has to be uploaded again to come back. One confirmation covers the whole
 * selection, so the subtitle names what is going.
 *
 * A batch is deleted one document at a time and the API refuses per document
 * (409, 503), so a partial success is the normal case: whatever was deleted is
 * already gone from the list behind the dialog, and the dialog stays up
 * reporting the rest, with the confirm button retrying just those.
 */
export function DeleteDocumentDialog({
  documents,
  progress,
  failures,
  onCancel,
  onConfirm,
}: DeleteDocumentDialogProps) {
  const isSubmitting = progress !== null;
  const count = documents.length;
  const isSingle = count === 1;
  const isAnnotation =
    isSingle && documents[0].document_type === "UPLOADED_EVALUATION";
  const hasFailures = failures.length > 0;

  const confirmLabel = () => {
    if (progress) {
      return isSingle
        ? "Deleting…"
        : `Deleting ${Math.min(progress.completed + 1, progress.total)} of ${
            progress.total
          }…`;
    }
    if (hasFailures) {
      return isSingle ? "Try again" : `Try again (${count})`;
    }
    return isSingle ? "Delete document" : `Delete ${count} documents`;
  };

  return (
    <Dialog
      title={isSingle ? "Delete this document?" : `Delete ${count} documents?`}
      subtitle={
        <span className="font-mono">{summariseFilenames(documents)}</span>
      }
      onClose={onCancel}
      maxWidth="max-w-lg"
      ariaLabelledBy="delete-document-title"
    >
      <div className="flex flex-col gap-4">
        <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
          <li>
            {isAnnotation
              ? "The model stops using this annotation as guidance when it scores."
              : isSingle
                ? "The model stops drawing on this document when it scores."
                : "The model stops drawing on these documents when it scores."}
          </li>
          <li>
            Evaluations already scored keep their scores, but citations pointing
            at {isSingle ? "this document" : "them"} will no longer open.
          </li>
          <li>
            There is no undo — restoring {isSingle ? "it" : "them"} means
            uploading the {isSingle ? "file" : "files"} again.
          </li>
        </ul>

        {hasFailures && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm">
            <p className="font-medium text-destructive">
              {failures.length === 1
                ? "1 document was not deleted"
                : `${failures.length} documents were not deleted`}
            </p>
            <ul className="mt-1 flex flex-col gap-1 text-muted-foreground">
              {failures.map((failure) => (
                <li key={failure.documentId} className="break-words">
                  <span className="font-mono">{failure.filename}</span> —{" "}
                  {failure.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
            {hasFailures ? "Close" : "Cancel"}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {confirmLabel()}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
